import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	output,
	signal,
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
} from '@angular/material/table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DateAdapter, MatOption } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { combineLatest, filter } from 'rxjs';
import { EventVehicle } from '@/tourism-events/tourism-events.model';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { deepClone } from '@/_utils/deep-clone';

// these are not all the properties in the vehicle object,
// but I don't know the rest of the types returned by the PHP backend
// because they are returned null and there is no documentation for this
export interface TourismVehicle {
	id: string;
	seats: string;
	name: string;
	status_name: string;
}

type AddedVehicle = TourismVehicle & {
	dateFrom: PhpBackendDateFormat | null;
	dateTo: PhpBackendDateFormat | null;
};

export type TourismVehicleOutput = Pick<AddedVehicle, 'id' | 'dateFrom' | 'dateTo'>;

type PhpBackendDateFormat = `${string}-${string}-${string} ${string}:${string}:${string}`;

@Component({
	selector: 'app-tourism-events-vehicle-add',
	standalone: true,
	imports: [
		FormsModule,
		MatButton,
		MatCell,
		MatFormFieldModule,
		MatDatepickerModule,
		FormsModule,
		ReactiveFormsModule,
		MatTable,
		MatHeaderCell,
		MatColumnDef,
		MatHeaderCellDef,
		MatCellDef,
		MatHeaderRow,
		MatHeaderRowDef,
		MatRow,
		MatRowDef,
		DatePipe,
		MatAutocomplete,
		MatOption,
		MatInput,
		MatAutocompleteTrigger,
	],
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
	templateUrl: './tourism-events-vehicle-add.component.html',
	styleUrl: './tourism-events-vehicle-add.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourismEventsVehicleAddComponent implements OnInit {
	readonly fb = inject(FormBuilder);

	readonly displayedColumns: string[] = ['fullName', 'dateRange', 'remove'];

	readonly selectedVehicleWithRange: FormGroup = this.fb.group({
		selectedVehicle: [null, Validators.required],
		range: this.fb.group({
			dateFrom: [null, [Validators.required, this.validDateValidator]],
			dateTo: [null, [Validators.required, this.validDateValidator]],
			timeFrom: [null, [Validators.required]],
			timeTo: [null, [Validators.required]],
		}),
	});

	readonly editable = input<boolean>(false);
	readonly vehicles = input.required<TourismVehicle[]>();
	readonly min = input.required<PhpBackendDateFormat>();
	readonly max = input.required<PhpBackendDateFormat>();
	readonly alreadyAddedVehicles = input.required<EventVehicle[]>();

	readonly emitVehiclesArray = output<TourismVehicleOutput[]>();

	readonly addedVehicles = signal<AddedVehicle[]>([]);
	readonly dateMin = signal<Date>(null);
	readonly dateMax = signal<Date>(null);

	readonly availableDateRange = computed(() => ({
		min: this.dateMin() ?? (this.min() ? new Date(this.min()) : null),
		max: this.dateMax() ?? (this.max() ? new Date(this.max()) : null),
	}));

	readonly filteredVehicles = computed(() => {
		const name =
			typeof this.searchedVehicle() === 'string'
				? this.searchedVehicle()
				: (this.searchedVehicle() as TourismVehicle)?.name;
		return name ? this._filter(name as string) : this.vehicles();
	});

	readonly filteredVehicleToArray = computed(() =>
		this.searchedArrayVehicle() ? this._filter(this.searchedArrayVehicle()) : this.vehicles(),
	);

	readonly searchedArrayVehicle = signal('');

	readonly searchedVehicle = toSignal<string | TourismVehicle>(
		this.selectedVehicleWithRange.get('selectedVehicle').valueChanges,
	);

	private readonly infobox = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly datePipe = inject(DatePipe);

	private readonly tourismEventDateRange$ = combineLatest([toObservable(this.min), toObservable(this.max)]);

	private readonly alreadyAddedVehicles$ = toObservable(this.alreadyAddedVehicles).pipe(
		filter((value) => !!value && value.length > 0),
		takeUntilDestroyed(this.destroyRef),
	);

	constructor() {
		effect(() => {
			const mappedVehicle = this.addedVehicles().map(
				(vehicle): TourismVehicleOutput => ({
					id: vehicle.id,
					dateFrom: vehicle.dateFrom,
					dateTo: vehicle.dateTo,
				}),
			);

			this.emitVehiclesArray.emit(mappedVehicle);
		});

		effect(() => {
			if (this.min() && this.max() && !this.addedVehicles().length) {
				const [dateMin, timeMin] = this.min().split('T');
				const [dateMax, timeMax] = this.max().split('T');

				const range = this.selectedVehicleWithRange.get('range');

				range.get('dateFrom').setValue(new Date(dateMin));
				range.get('dateTo').setValue(new Date(dateMax));
				range.get('timeFrom').setValue(timeMin);
				range.get('timeTo').setValue(timeMax);
			}
		});

		effect(() => {
			if (this.editable()) {
				this.selectedVehicleWithRange.disable();
			} else {
				this.selectedVehicleWithRange.enable();
			}
		});
	}

	ngOnInit(): void {
		this.alreadyAddedVehicles$.subscribe((vehicles: EventVehicle[]) => {
			vehicles.forEach((vehicle) => {
				const foundVehicle = this.vehicles().find((v) => v.id === vehicle.vehicle_id);
				if (!foundVehicle) {
					return;
				}

				const vehicleToAdd = {
					...foundVehicle,
					dateFrom: vehicle.date_start as PhpBackendDateFormat,
					dateTo: vehicle.date_stop as PhpBackendDateFormat,
				};

				this.addedVehicles.set(this.sortByDateFrom<AddedVehicle>([...this.addedVehicles(), vehicleToAdd]));
			});

			this.updateDateLimits();
			this.resetToDefault(this.selectedVehicleWithRange);
		});

		this.tourismEventDateRange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateDateLimits());
	}

	addVehicle(): void {
		this.selectedVehicleWithRange.markAllAsTouched();
		if (this.selectedVehicleWithRange.invalid) {
			return;
		}

		if (this.isDateRangeOverlapping()) {
			this.infobox.error(
				`Błąd podczas dodawania pojazdu! <br> Wybrany zakres koliduje z zakresem innego pojazdu`,
			);
			return;
		}

		const range = this.selectedVehicleWithRange.get('range');
		const { dateFrom: selectedFrom, dateTo: selectedTo, timeFrom, timeTo } = range.value;

		const formattedFrom = this.getFormattedDate(selectedFrom, timeFrom);
		const formattedTo = this.getFormattedDate(selectedTo, timeTo);

		if (
			this.getDateFromPhpBackendFormat(formattedFrom).getTime() >
			this.getDateFromPhpBackendFormat(formattedTo).getTime()
		) {
			this.infobox.error(
				`Błąd podczas dodawania pojazdu! <br> Wybrana data od (${this.datePipe.transform(this.getDateFromPhpBackendFormat(formattedFrom), 'dd.MM.yyyy, HH:mm')})
 				jest większa niż data do (${this.datePipe.transform(this.getDateFromPhpBackendFormat(formattedTo), 'dd.MM.yyyy, HH:mm')})`,
			);
			return;
		}

		const { selectedVehicle } = this.selectedVehicleWithRange.value;
		const vehicleToAdd = {
			...selectedVehicle,
			dateFrom: formattedFrom,
			dateTo: formattedTo,
		};

		this.addedVehicles.set(this.sortByDateFrom([...this.addedVehicles(), vehicleToAdd]));
		this.resetToDefault(this.selectedVehicleWithRange);
		this.updateDateLimits();
	}

	removeVehicleByIndex(index: number): void {
		const vehicles = deepClone(this.addedVehicles());

		if (index >= 0 && index < vehicles.length) {
			vehicles.splice(index, 1);
			this.addedVehicles.set(vehicles);
			this.updateDateLimits();
		}
	}

	changeVehicle(index: number, vehicle: AddedVehicle | string): void {
		if (typeof vehicle === 'string') {
			this.searchedArrayVehicle.set(vehicle);
			return;
		}
		this.searchedArrayVehicle.set('');
		const newAddedVehicles = [...this.addedVehicles()];
		const { dateTo, dateFrom } = this.addedVehicles()[index];
		newAddedVehicles[index] = { ...vehicle, dateFrom, dateTo };
		this.addedVehicles.set(newAddedVehicles);
	}

	displayFn(vehicle: TourismVehicle): string {
		if (!!vehicle) {
			return `${vehicle.name}${!!vehicle.seats ? ` (${vehicle.seats}) ` : ''} - ${vehicle.status_name}`;
		}
		return '';
	}

	private getFormattedDate(dateStr: string, timeStr: `${string}:${string}:${string}`): PhpBackendDateFormat {
		const date = new Date(dateStr);

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');

		return `${year}-${month}-${day} ${timeStr}`;
	}

	private getDateFromPhpBackendFormat(dateTime: PhpBackendDateFormat): Date {
		return new Date(dateTime);
	}

	private isDateRangeOverlapping(): boolean {
		const range = this.selectedVehicleWithRange.get('range');
		const { dateFrom: selectedFrom, dateTo: selectedTo, timeFrom, timeTo } = range.value;
		const formattedFrom = this.getFormattedDate(selectedFrom, timeFrom);
		const formattedTo = this.getFormattedDate(selectedTo, timeTo);

		return this.addedVehicles().some(({ dateFrom, dateTo }) => {
			// Check if the selected date range overlaps with the current range
			return (
				this.getDateFromPhpBackendFormat(formattedFrom) < this.getDateFromPhpBackendFormat(dateTo) &&
				this.getDateFromPhpBackendFormat(formattedTo) > this.getDateFromPhpBackendFormat(dateFrom)
			);
		});
	}

	private _filter(name: string): TourismVehicle[] {
		const filterValue = name.toLowerCase();
		return this.vehicles().filter((option) => option.name.toLowerCase().includes(filterValue));
	}

	private updateMinimumDate(): void {
		if (
			!this.addedVehicles().length ||
			this.getDateFromPhpBackendFormat(this.addedVehicles()[0]?.dateFrom).getTime() !==
				this.getDateFromPhpBackendFormat(this.min()).getTime()
		) {
			this.dateMin.set(this.min() ? this.getDateFromPhpBackendFormat(this.min()) : null);
			return;
		}

		let minDate = this.addedVehicles()[0].dateTo;

		for (let i = 1; i < this.addedVehicles().length; i++) {
			const currentDateFrom = this.getDateFromPhpBackendFormat(this.addedVehicles()[i].dateFrom).getTime();

			if (currentDateFrom === this.getDateFromPhpBackendFormat(minDate).getTime()) {
				minDate = this.addedVehicles()[i].dateTo;
			} else {
				break;
			}
		}

		this.dateMin.set(this.getDateFromPhpBackendFormat(minDate));
	}

	private updateMaximumDate(): void {
		if (
			!this.addedVehicles().length ||
			this.getDateFromPhpBackendFormat(this.addedVehicles()[this.addedVehicles().length - 1].dateTo).getTime() !==
				this.getDateFromPhpBackendFormat(this.max()).getTime()
		) {
			this.dateMax.set(this.max() ? this.getDateFromPhpBackendFormat(this.max()) : null);
			return;
		}

		let maxDate = this.getDateFromPhpBackendFormat(this.addedVehicles()[this.addedVehicles().length - 1].dateFrom);

		for (let i = this.addedVehicles().length - 1; i > 0; i--) {
			const currentDateFrom = this.getDateFromPhpBackendFormat(this.addedVehicles()[i].dateFrom);
			const previousDateTo = this.getDateFromPhpBackendFormat(this.addedVehicles()[i - 1].dateTo).getTime();

			if (currentDateFrom.getTime() === previousDateTo) {
				maxDate = this.getDateFromPhpBackendFormat(this.addedVehicles()[i - 1].dateFrom);
			} else {
				break;
			}
		}

		this.dateMax.set(maxDate);
	}

	private updateDateLimits() {
		this.updateMinimumDate();
		this.updateMaximumDate();
	}

	private validDateValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		return value instanceof Date && !isNaN(value.getTime()) ? null : { invalidDate: true };
	}

	private resetToDefault(formGroup: FormGroup): void {
		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.get(key);

			if (control instanceof FormGroup) {
				this.resetToDefault(control);
			} else if (control instanceof FormControl) {
				control.setValue(null);
			}
		});
		this.selectedVehicleWithRange.markAsUntouched();
	}

	private sortByDateFrom<T>(dateRanges: (T & { dateFrom: string; dateTo: string })[]): T[] {
		return dateRanges.sort((a, b) => new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime());
	}
}
